// ─── ClientPicker ───────────────────────────────────────────
// Typeahead avec chips pour sélectionner 1 ou N clients sans charger
// la liste complète. Conçu pour rester performant à 2000+ clients :
//   - Aucun fetch tant que le picker n'est pas ouvert
//   - Top 20 clients (alpha) à l'ouverture, refetch debounce 200ms
//     en mode `lite` (Id+Name uniquement → backend ne fait pas la
//     dérivation N+1 sur sites/agents/alertes).
//   - Multi-sélection : chips amovibles + suggestions filtrées en
//     excluant les déjà sélectionnés.
//   - Single-sélection : afficher le nom du sélectionné dans l'input,
//     bouton × pour clear.
//
// Props :
//   value     : array<id> en multi, string|null en single
//   onChange  : (newValue) => void
//   multiple  : bool (default false)
//   placeholder : string
//   className : string (sur le wrapper)
//   disabled  : bool

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { X, Search } from 'lucide-react';
import { api } from '../lib/api.jsx';

const TOP_LIMIT = 20;
const DEBOUNCE_MS = 200;

export function ClientPicker({
  value,
  onChange,
  multiple = false,
  placeholder = 'Rechercher un client…',
  className = '',
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  // Cache id → name pour résoudre les chips même quand le client n'est pas
  // dans le set de résultats courant (ex : sélectionné lors d'une recherche
  // précédente, pas dans le top 20 alpha).
  const [labels, setLabels] = useState({});
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  const selectedIds = useMemo(() => {
    if (multiple) return Array.isArray(value) ? value : [];
    return value ? [value] : [];
  }, [value, multiple]);

  // Résoudre les labels manquants pour `value` au mount (en mode lite)
  useEffect(() => {
    const missing = selectedIds.filter((id) => !labels[id]);
    if (missing.length === 0) return;
    let active = true;
    // Pas d'endpoint bulk by ID — on fait un fetch lite des résultats triés
    // alpha et on prend ce qu'on peut. Pour une feature plus robuste : ajouter
    // un endpoint /api/clients/by-ids?ids=…. Pour 1-N IDs c'est OK : on fetch
    // lite paginé jusqu'à les trouver, ou on accepte que le label montre l'ID.
    api.get('/clients?lite=true&limit=1000').then((r) => {
      if (!active || !Array.isArray(r)) return;
      const map = {};
      for (const c of r) map[c.id] = c.name;
      setLabels((prev) => ({ ...map, ...prev }));
    });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(selectedIds)]);

  // Fetch debounced quand le picker est ouvert
  useEffect(() => {
    if (!open) return;
    let active = true;
    setLoading(true);
    const t = setTimeout(async () => {
      const q = query.trim();
      const path = q
        ? '/clients?lite=true&limit=' + TOP_LIMIT + '&search=' + encodeURIComponent(q)
        : '/clients?lite=true&limit=' + TOP_LIMIT;
      const r = await api.get(path);
      if (!active) return;
      const list = Array.isArray(r) ? r : [];
      setResults(list);
      setLabels((prev) => {
        const next = { ...prev };
        for (const c of list) next[c.id] = c.name;
        return next;
      });
      setLoading(false);
    }, DEBOUNCE_MS);
    return () => {
      active = false;
      clearTimeout(t);
    };
  }, [query, open]);

  // Fermer au clic en dehors
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const select = (client) => {
    if (multiple) {
      const cur = Array.isArray(value) ? value : [];
      if (!cur.includes(client.id)) onChange([...cur, client.id]);
      setQuery('');
      inputRef.current?.focus();
    } else {
      onChange(client.id);
      setQuery('');
      setOpen(false);
    }
  };

  const remove = (id) => {
    if (multiple) {
      const cur = Array.isArray(value) ? value : [];
      onChange(cur.filter((x) => x !== id));
    } else {
      onChange(null);
    }
  };

  // Suggestions filtrées (exclut les déjà sélectionnés en mode multi)
  const suggestions = useMemo(() => {
    if (!multiple) return results;
    const sel = new Set(selectedIds);
    return results.filter((c) => !sel.has(c.id));
  }, [results, selectedIds, multiple]);

  // Mode single : si un client est sélectionné, l'afficher dans l'input
  const singleLabel = !multiple && selectedIds[0] ? labels[selectedIds[0]] || selectedIds[0] : '';

  return (
    <div ref={wrapperRef} className={'relative ' + className}>
      <div
        className={`bg-sidebar border border-rule rounded-lg px-2.5 py-1.5 flex items-center flex-wrap gap-1.5 min-h-[36px] ${
          open ? 'border-blue-500' : ''
        } ${disabled ? 'opacity-60 cursor-default' : 'cursor-text'}`}
        onClick={() => {
          if (!disabled) {
            setOpen(true);
            inputRef.current?.focus();
          }
        }}
      >
        {/* Chips en mode multi */}
        {multiple &&
          selectedIds.map((id) => (
            <span
              key={id}
              className="inline-flex items-center gap-1 bg-blue-500/15 border border-blue-500/30 text-blue-400 rounded text-[11px] px-2 py-0.5 font-medium"
            >
              {labels[id] || id}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  remove(id);
                }}
                disabled={disabled}
                className="bg-transparent border-none text-blue-400 cursor-pointer p-0 inline-flex items-center hover:text-blue-300"
                title="Retirer"
              >
                <X size={11} strokeWidth={2.5} />
              </button>
            </span>
          ))}

        {/* Mode single : valeur sélectionnée affichée comme texte */}
        {!multiple && singleLabel && !open && (
          <span className="text-fg-2 text-xs font-medium flex-1">{singleLabel}</span>
        )}

        {/* Input de recherche (caché en single quand fermé) */}
        {(multiple || open || !singleLabel) && (
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => !disabled && setOpen(true)}
            placeholder={multiple || !singleLabel ? placeholder : ''}
            disabled={disabled}
            className="bg-transparent border-none outline-none text-fg-2 text-xs flex-1 min-w-[80px]"
          />
        )}

        {/* Bouton clear en mode single */}
        {!multiple && singleLabel && !open && !disabled && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              remove(selectedIds[0]);
            }}
            className="bg-transparent border-none text-muted-2 cursor-pointer p-0 inline-flex items-center hover:text-fg-2"
            title="Effacer"
          >
            <X size={13} strokeWidth={2} />
          </button>
        )}

        <Search size={12} strokeWidth={1.75} className="text-muted-3 ml-auto" />
      </div>

      {/* Dropdown suggestions */}
      {open && (
        <div className="absolute z-[60] left-0 right-0 mt-1 bg-card border border-rule rounded-lg shadow-2xl max-h-[260px] overflow-y-auto">
          {loading && suggestions.length === 0 ? (
            <div className="px-3 py-2 text-xs text-muted-2">Recherche…</div>
          ) : suggestions.length === 0 ? (
            <div className="px-3 py-2 text-xs text-muted-2">
              {query ? 'Aucun client trouvé' : 'Aucun client disponible'}
            </div>
          ) : (
            suggestions.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => select(c)}
                className="w-full text-left bg-transparent border-none px-3 py-1.5 text-xs text-fg-2 cursor-pointer hover:bg-rule"
              >
                {c.name}
              </button>
            ))
          )}
          {!query && results.length === TOP_LIMIT && (
            <div className="px-3 py-1.5 text-[10px] text-muted-3 border-t border-rule">
              Tapez pour affiner — {TOP_LIMIT} premiers affichés
            </div>
          )}
        </div>
      )}
    </div>
  );
}
